import { ReactNode } from 'react';
import { cn } from '../../utils';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

interface ButtonProps {
  onBtnClick?: React.MouseEventHandler<HTMLButtonElement>;
  buttonText?: string;
  children?: ReactNode;
  addOnClass?: string;
  addOnTextClass?: string;
  disabled?: boolean;
  type?: 'primary' | 'secondary';
  childrenPosition?: 'before' | 'after';
  loading?: boolean;
  size?: 'default' | 'small';
  buttonType?: 'button' | 'submit' | 'reset';
}

/**
 * A reusable button component that can be used throughout the application.
 *
 * @param {ButtonProps} props - The properties for the Button component.
 * @param {() => void} props.onBtnClick - The click handler for the button.
 * @param {string} [props.buttonText=''] - The text to be displayed inside the button.
 * @param {ReactNode} [props.children] - The optional icon or other elements to be displayed.
 * @param {string} [props.addOnClass=''] - Additional class names to be applied to the button.
 * @param {string} [props.addOnTextClass=''] - Additional class names to be applied to the button text.
 * @param {boolean} [props.disabled=false] - Whether the button is disabled.
 * @param {type} [props.childrenPosition=''] - Position of children relative to the button text. Default is 'before'.
 * @param {type} [props.size=''] - Size of button. Default is 'default'.
 * @returns {JSX.Element} - The rendered Button component.
 */
const Button: React.FC<ButtonProps> = ({
  onBtnClick,
  children,
  addOnClass = '',
  addOnTextClass = '',
  buttonText = '',
  disabled = false,
  childrenPosition = 'before',
  loading = false,
  size = 'default',
  buttonType = 'submit',
}) => {
  let content = (
    <>
      {childrenPosition === 'before' && children}
      {buttonText && (
        <span
          className={cn(
            'text-xs font-semibold stroke-inherit text-white whitespace-nowrap tracking-wider',
            addOnTextClass,
            size === 'small' ? 'text-[10px]' : '',
          )}
        >
          {buttonText}
        </span>
      )}
      {childrenPosition === 'after' && children}
    </>
  );

  if (loading) {
    content = (
      <Spin
        wrapperClassName={cn('text-white', addOnTextClass)}
        indicator={<LoadingOutlined spin className={cn('text-white', addOnTextClass)} />}
      />
    );
  }

  return (
    <button
      className={cn(
        'outline-none flex gap-2 items-center justify-center py-1.5 px-6 rounded-md shadow-md hover:opacity-90 transition-opacity ease-in-out active:scale-[0.98] cursor-pointer',
        addOnClass,
        size === 'small' ? 'px-2 py-0.5 text-[10px] gap-x-1' : '',
        {
          'active:scale-[1] hover:opacity-60 opacity-60 cursor-not-allowed':
            disabled || loading,
        },
      )}
      onClick={onBtnClick}
      disabled={disabled}
      type={buttonType}
    >
      {content}
    </button>
  );
};

export default Button;
